import { Box, Button, Group, Modal, NumberInput, Stack, Table, Text, Title } from '@mantine/core';
import { formatDateTime } from '@medplum/core';
import { Observation, Patient } from '@medplum/fhirtypes';
import { Document, Form, useMedplum } from '@medplum/react';
import { useState, type JSX } from 'react';
import { LineChart } from '../../../components/LineChart';

export function MoodTracking(): JSX.Element {
  const medplum = useMedplum();
  const profile = medplum.getProfile() as Patient;
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  // Fetch mood observations
  const observations = medplum
    .searchResources('Observation', 'code=mood-score&patient=' + profile.id)
    .read();

  // Prepare chart data
  const chartData = {
    labels: observations.map((obs) => formatDateTime(obs.effectiveDateTime as string)),
    datasets: [
      {
        label: 'Mood Score',
        data: observations.map((obs) => obs.valueInteger as number),
        backgroundColor: 'rgba(29, 112, 214, 0.7)',
        borderColor: 'rgba(29, 112, 214, 1)',
      },
    ],
  };

  const handleSubmit = (formData: Record<string, string>): void => {
    const score = parseInt(formData.moodScore, 10);
    
    const observation: Observation = {
      resourceType: 'Observation',
      status: 'preliminary',
      code: {
        coding: [
          {
            system: 'http://loinc.org',
            code: 'mood-score',
            display: 'Mood Score',
          },
        ],
        text: 'Mood Score',
      },
      subject: {
        reference: `Patient/${profile.id}`,
      },
      effectiveDateTime: new Date().toISOString(),
      valueInteger: score,
    };

    medplum.createResource(observation).then(() => {
      setModalOpen(false);
      window.location.reload();
    });
  };

  return (
    <Document>
      <Group justify="space-between" mb="xl">
        <Title>Mood Tracking</Title>
        <Button onClick={() => setModalOpen(true)}>Log Mood</Button>
      </Group>

      {observations.length > 0 && <LineChart chartData={chartData} />}

      <Box my="xl">
        <Title order={3} mb="md">History</Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Mood Score</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {observations.map((obs) => (
              <Table.Tr key={obs.id}>
                <Table.Td>{formatDateTime(obs.effectiveDateTime as string)}</Table.Td>
                <Table.Td>{obs.valueInteger}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Log Your Mood"
      >
        <Form onSubmit={handleSubmit}>
          <Stack>
            <Text>How are you feeling today?</Text>
            <Text size="sm" c="dimmed">Rate your mood from 1 (lowest) to 10 (highest)</Text>
            <NumberInput
              name="moodScore"
              min={1}
              max={10}
              defaultValue={5}
              required
            />
            <Group justify="flex-end">
              <Button type="submit">Save</Button>
            </Group>
          </Stack>
        </Form>
      </Modal>
    </Document>
  );
}